/** SUNTRONIX ONE — Public feedback capture page (§49-52).
 *  Reused HMAC token pattern; unauthenticated. */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, CheckCircle2, AlertTriangle } from "lucide-react";

export default function PublicFeedback() {
  const { token } = useParams();
  const [view, setView] = useState(null);
  const [err, setErr] = useState(null);
  const [rating, setRating] = useState(0);
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/feedback/${token}`);
        setView(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "This link is invalid or expired.");
      }
    })();
  }, [token]);

  const submit = async () => {
    if (!rating) { toast.error("Please give a rating"); return; }
    setBusy(true);
    try {
      await api.post(`/public/feedback/${token}`, { rating, comments, from_role: "employee" });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally { setBusy(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="pf-error">
      <div className="max-w-md w-full bg-white border p-6 rounded-lg text-center">
        <AlertTriangle className="h-8 w-8 text-amber-600 mx-auto" strokeWidth={1.5} />
        <div className="mt-3 text-sm text-slate-700">{err}</div>
      </div>
    </div>
  );
  if (!view) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  if (done) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="pf-done">
      <div className="max-w-md w-full bg-white border border-emerald-200 p-6 rounded-lg text-center">
        <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" strokeWidth={1.5} />
        <div className="mt-3 text-base font-medium">Thanks — your rating is in.</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 p-4">
      <div className="max-w-md mx-auto bg-white border p-5 rounded-lg" data-testid="pf">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">SUNTRONIX ONE</div>
        <div className="mt-1 text-lg font-medium text-slate-900">How was your deployment?</div>
        <div className="mt-1 text-[12px] text-slate-500">{view.case.customer} · Case {view.case.case_code}</div>

        <div className="mt-5 flex gap-2 justify-center">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => setRating(n)} data-testid={`pf-star-${n}`}
              className={n <= rating ? "text-amber-500" : "text-slate-300"}>
              <Star className="h-8 w-8" fill={n <= rating ? "currentColor" : "none"} strokeWidth={1.4} />
            </button>
          ))}
        </div>
        <Textarea rows={3} className="mt-4" placeholder="Any comments?"
          value={comments} onChange={(e) => setComments(e.target.value)} data-testid="pf-comments" />
        <Button onClick={submit} disabled={busy} data-testid="pf-submit"
          className="w-full mt-4"
          style={{ background: "var(--st-accent, #B7803F)", color: "white" }}>
          {busy ? "Submitting…" : "Submit"}
        </Button>
      </div>
    </div>
  );
}
